/**
 * ポータルの画面（ヘッダー・左パネル・3D ビュー・タイムライン・管理パネル）。
 * HTML の構造・クラス名・文言はサンプル WebUI-Sample/3D-Portal-Google3D.html（246〜334 行と 677〜1050 行）と同じ。
 * 3D 描画（CesiumJS）は src/render/cesium/ に閉じ込め、ここからは options.createScene で受け取る。
 * 3D が使えない環境（WebGL がない、テストの jsdom など）でも、台帳・土量・工程・予測は使える（FR-VIEW-10）。
 */
import type { AddressIndex, PlaceHit } from '../app/addressSearch';
import type { Edition } from '../app/edition';
import type { Change, Chip, SiteView, Speed, Store } from '../app/store';
import { isGradeKey, isPlanNumberKey } from '../app/planInput';
import {
  escapeHtml as esc,
  formatFixed1 as fmt1,
  formatInt as fmtN,
  safeColor,
} from '../core/text';
import { dayNumber, formatDay as ds } from '../core/time';
import type { DayNumber } from '../core/time';
import { B, REGIONS, T_MAX, T_MIN, actualAtBase, planAtBase } from '../model';
import type { Region } from '../model';
import {
  capHtml,
  dockHeaderHtml,
  dockTabsHtml,
  earthBalHtml,
  earthKpiHtml,
  portfolioTableHtml,
  schedNoteHtml,
  wmStateHtml,
  wmWhatIfHtml,
  whatIfNote,
} from './dockTabs';
import type { DockTab } from './dockTabs';
import {
  cutFillMap,
  dayAtGanttX,
  ganttGeom,
  massCurve,
  massNote,
  sCurve,
  siteGantt,
} from './siteCharts';
import { portfolioGantt } from './charts';
import {
  KEY_RE,
  KEY_STORE,
  fictionText,
  helpHtml,
  keyDiagHtml,
  keyDialogHtml,
  layersHtml,
  legendHtml,
  toolsHtml,
} from './viewChrome';
import type { ChromeState } from './viewChrome';

/** 予測日。表示期間（T_MAX）より先は「（表示期間外）」を添える（計算値は変えない。仕様の未決事項 OI-1）。 */
const forecastDay = (d: DayNumber): string => (d > T_MAX ? `${ds(d)}（表示期間外）` : ds(d));
const winterTxt = (r: Region): string =>
  r.ws ? `${r.ws}月〜${r.we}月（土工稼働 ${Math.round(r.wf * 100)}%）` : 'なし';

function delayBadge(delay: number): string {
  if (delay > 14) return `<span class="badge late">予測 +${delay}日</span>`;
  if (delay > 0) return `<span class="badge warn">予測 +${delay}日</span>`;
  return '<span class="badge ok">計画内</span>';
}

/** 検索の文字に一致した部分を強調する（サンプルの hl）。 */
function hl(text: string, q: string): string {
  const needle = q.trim();
  const i = needle ? text.toLowerCase().indexOf(needle.toLowerCase()) : -1;
  if (i < 0) return esc(text);
  return `${esc(text.slice(0, i))}<mark>${esc(text.slice(i, i + needle.length))}</mark>${esc(text.slice(i + needle.length))}`;
}

/** 3D ビューの部品（src/render/cesium/CesiumScene の必要な部分だけ）。テストでは渡さない。 */
export interface Scene3D {
  readonly mode: 'nation' | 'site';
  readonly layers: Readonly<Record<string, boolean>>;
  readonly google: boolean;
  autoRotate: boolean;
  sceneDirty: boolean;
  terrainDirty: boolean;
  mapDirty: boolean;
  useG(): boolean;
  enterSite(v: SiteView, viaNation: boolean): void;
  enterNation(prev: SiteView | undefined): void;
  applyView(view: string): void;
  northUp(): void;
  setLayer(k: string, on: boolean): void;
  flyPref(name: string, dur?: number): void;
  flyHome(dur?: number): void;
  flyPoint(lon: number, lat: number): void;
  designChanged(): void;
  connectGoogle(key: string): Promise<boolean>;
  disconnectGoogle(): void;
  geoOf(s: SiteView['site']): { geo: number; geoCal: boolean };
}

function shell(edition: Edition, store: Store): string {
  const counts = store.prefCounts();
  const prefs = Object.keys(REGIONS);
  const bp = ((B - T_MIN) / (T_MAX - T_MIN)) * 100;
  const pos = (p: number) => `calc(8px + (100% - 16px) * ${p})`;
  const years = [2026, 2027, 2028]
    .map(
      (y) =>
        `<span class="tlmark" style="left:${pos((dayNumber(`${y}-01-01`) - T_MIN) / (T_MAX - T_MIN))}">${y}</span>`,
    )
    .join('');
  return `<header>
    <h1>3D現場ポータル</h1>
    <span class="tag jp">全国版</span>
    <span class="tag wm">WORLD MODEL</span>
    <span class="tag jp">${esc(edition.tag)}</span>
    <span class="tag">SAMPLE・架空の工事</span>
    <div class="spacer"></div>
    <span class="basedate" id="basedate">基準日（実績の最終観測）${ds(B)}</span>
    <span class="net init" id="netStatus">3D 準備中</span>
    <button class="btn ghost" id="keyBtn" title="Google Map Tiles API のキーを設定"${edition.key === 'offline' ? ' hidden' : ''}>🔑 APIキー</button>
  </header>

  <aside>
    <div class="search">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
      <input id="q" type="search" placeholder="工事名・現場ID・住所で検索" autocomplete="off" aria-label="現場・住所検索">
      <kbd>/</kbd>
    </div>
    <div class="filters" id="chips">
      <select id="prefSel" aria-label="都道府県で絞り込み"><option value="">全国（${store.views.length}現場）</option><optgroup label="現場のある都道府県">${prefs
        .filter((p) => counts.has(p))
        .map((p) => `<option value="${esc(p)}">${esc(p)}（${counts.get(p)}）</option>`)
        .join('')}</optgroup><optgroup label="その他">${prefs
        .filter((p) => !counts.has(p))
        .map((p) => `<option value="${esc(p)}">${esc(p)}（0）</option>`)
        .join('')}</optgroup></select>
      <button class="chip on" data-chip="all">すべて</button>
      <button class="chip" data-chip="建築">建築</button>
      <button class="chip" data-chip="土木">土木</button>
      <button class="chip" data-chip="delay">遅延予測</button>
    </div>
    <div class="count"><span id="ledgerTitle">現場台帳</span><span id="count"></span></div>
    <div id="list" role="listbox" aria-label="現場台帳"></div>
    <div id="summary"></div>
    <div class="srcfoot">出典：国土数値情報（行政区域・高速道路時系列）国土交通省／アドレス・ベース・レジストリ（デジタル庁）を加工した Geolonia 住所データ（CC BY 4.0）／国土地理院 標高タイルを加工して作成／3D都市・地形：Google（帰属表示は3Dビュー左下）／描画：CesiumJS</div>
  </aside>

  <section id="main">
    <div id="view">
      <div id="cz" aria-label="3Dビュー"></div>
      <div id="labels"></div>
      <div class="compass" id="compass" title="北を上に戻す">
        <svg id="needle" width="28" height="28" viewBox="-15 -15 30 30"><path d="M0-12 L5 2 L0 0 L-5 2Z" fill="#ef4444"/><path d="M0 12 L5-2 L0 0 L-5-2Z" fill="#cbd5e1"/></svg>
      </div>
      <div class="layers" id="layers"></div>
      <div class="tools" id="tools"></div>
      <div class="scalebar" id="scalebar"></div>
      <div class="modeflag obs" id="modeflag">実績（観測）</div>
      <div class="ovl legend" id="legend"></div>
      <div class="ovl help" id="help"></div>
      <div class="fiction" id="fiction" hidden>工事・建物計画はすべて架空のサンプルです</div>
      <div class="toast" id="toast" hidden></div>
      <div id="nogl" hidden>このブラウザではWebGLが利用できないため3D表示できません。<br>台帳・土量・工程などの管理機能は利用できます。</div>
    </div>

    <div id="timeline">
      <button class="tlbtn" id="tlNow" title="基準日（実績の最終観測日）へ戻る">基準日</button>
      <button class="tlbtn" id="tlPlay" title="Space">▶ 再生</button>
      <select id="tlSpeed" aria-label="再生速度">
        <option value="7">7日/秒</option><option value="30" selected>30日/秒</option><option value="90">90日/秒</option>
      </select>
      <div class="tlwrap" id="tlwrap">
        <input type="range" id="tlRange" aria-label="ワールド時刻" min="${T_MIN}" max="${T_MAX}" step="1" value="${B}" style="--bp:${bp}%">
        <div class="tlbase" id="tlBase" style="left:${pos(bp / 100)}"></div>
        ${years}<span class="tlmark" style="left:${pos(bp / 100)};bottom:-1px;color:#fff">基準日</span>
      </div>
      <span class="tldate" id="tlDate"></span>
      <span class="tlmode obs" id="tlMode">実績</span>
    </div>

    <div id="dock">
      <div class="dtabs" id="dtabs"></div>
      <div id="dockBody"></div>
    </div>
  </section>
${keyDialogHtml()}`;
}

/** 住所照合の候補（元仕様 §9）。台帳の行より上に出す。 */
function addressHtml(hits: readonly PlaceHit[]): string {
  return hits
    .map(
      (c, i) =>
        `<div class="addr"><span>📍 <b>${esc(c.label)}</b><small>ABR代表点（${esc(c.level)}）・オフライン照合</small></span><button class="btn" data-addr="${i}">地図で表示</button></div>`,
    )
    .join('');
}

function ledgerHtml(store: Store, hits: readonly PlaceHit[]): string {
  const { q, sel, T } = store.state;
  const list = store.filtered();
  return (
    addressHtml(hits) +
    (list.length
      ? list
          .map((v) => {
            const s = v.site;
            const p = Math.round(v.state.overall * 100);
            const pl = Math.round(v.state.plan * 100);
            const color = safeColor(s.color);
            const on = sel === s.id;
            return `<button class="row${on ? ' sel' : ''}" data-id="${esc(s.id)}" role="option" aria-selected="${on}">
      <div class="r1"><span class="dot" style="background:${color}"></span><span class="sid">${hl(s.id, q)}</span><span>${esc(s.type)}・${esc(s.kind.label)}</span>${delayBadge(v.cur.delay)}</div>
      <div class="nm">${hl(s.name, q)}</div><div class="ad">${hl(s.addr, q)}</div>
      <div class="r3"><div class="bar" title="${T > B ? '予測' : '実績'} ${p}% ／ 計画 ${pl}%"><i style="width:${p}%;background:${color}"></i><b style="left:calc(${pl}% - 1px)"></b></div><span>${p}%</span></div></button>`;
          })
          .join('')
      : '<div class="empty">該当する現場がありません</div>')
  );
}

/** 3D がないとき（テスト・WebGL なし）だけ出す、住所の印の文字表示。3D があれば地図に印を出す。 */
function markHtml(store: Store): string {
  const m = store.state.mark;
  if (!m) return '';
  return `<div class="mark-sum" role="status">📍 住所 <b>${esc(m.label)}</b><span class="sub">${m.lat.toFixed(4)}, ${m.lon.toFixed(4)}（ABR 代表点）</span> <button class="btn ghost" data-mark-clear>印を消す</button></div>`;
}

function summaryHtml(store: Store, scene: Scene3D | null): string {
  const v = store.selected;
  const useG = scene?.useG() ?? false;
  if (!v) {
    const sm = store.summary();
    const pref = store.state.pref;
    const r = pref ? REGIONS[pref] : undefined;
    return `<div class="dhead" style="margin-bottom:6px">${pref ? esc(pref) : '全国'}（基準日 ${ds(B)}）</div>
      <div class="kpis"><div class="kpi"><div class="v">${sm.count}</div><div class="l">稼働現場</div></div>
        <div class="kpi"><div class="v">${sm.avgActualPct}%</div><div class="l">平均進捗（実績）</div></div>
        <div class="kpi"><div class="v ${sm.late ? 'late' : 'ahead'}">${sm.late}</div><div class="l">遅延予測（14日超）</div></div>
        <div class="kpi"><div class="v">${fmtN(sm.cut / 1000)}<small style="font-size:11px;color:var(--muted)"> 千m³</small></div><div class="l">切土量 合計（地山）</div><div class="s">残土 ${fmtN(sm.spoil)} m³（ほぐし）</div></div></div>
      ${r ? `<table class="t" style="margin-bottom:10px"><tr><td>雨天・休止率</td><td>${r.rain}%</td></tr><tr><td>冬期</td><td>${esc(winterTxt(r))}</td></tr><tr><td>残土運搬距離</td><td>${r.dExt} km（標準）</td></tr></table>` : ''}
      ${scene ? '' : markHtml(store)}
      <p class="hint">日本地図の現場ピン、または台帳の行を選ぶと、その現場の3D（周辺は Google 実写3D）へ移動します。都道府県をクリックすると絞り込みます。${useG ? 'Google 3D 表示中は、地図への住所表示（ABR 照合）を止めています（Google 以外のジオコーディングを併用しないため）。' : '検索欄に住所を入れると、ABRの代表点で位置をオフライン照合します。'}</p>
      <div class="foot">現場名・番地・発注者・工程・建物計画はすべて架空（サンプル）です。位置は実在の町字代表点、施工範囲の地形は国土地理院の標高データ、周辺の街並みは Google の実写3D（オンライン）です。地域係数はサンプル値です。</div>`;
  }
  const s = v.site;
  const a = Math.round(actualAtBase(s) * 100);
  const pl = Math.round(planAtBase(s) * 100);
  const d = v.cur.delay;
  let emax = -Infinity;
  for (const e of s.g.E) if (e > emax) emax = e;
  const geo = scene?.geoOf(s);
  const height =
    useG && geo
      ? geo.geoCal
        ? `Google 地表に合わせ補正済（ジオイド高 ${fmt1(geo.geo)} m）`
        : `概算（ジオイド高 ${fmt1(geo.geo)} m）・現場表示後に自動補正`
      : '簡易地形モード（国土地理院 標高）';
  return `<div class="dhead"><span class="dot" style="background:${safeColor(s.color)}"></span><span class="sid">${esc(s.id)}</span><span>・${esc(s.type)}</span>${delayBadge(d)}</div>
    <h2>${esc(s.name)}</h2>
    <dl><dt>所在地</dt><dd>${esc(s.addr)}<br><span style="color:var(--muted);font-size:11px">番地はダミー。位置＝ABR 町字代表点（${esc(s.machiaza)}）<br>${s.lat.toFixed(5)}, ${s.lon.toFixed(5)}</span></dd>
      <dt>発注者</dt><dd>${esc(s.client)}</dd><dt>構造・規模</dt><dd>${esc(s.scale)}</dd>
      <dt>計画工期</dt><dd>${ds(s.planStart)} 〜 ${ds(s.planEnd)}</dd>
      <dt>施工範囲</dt><dd>${s.area.w} × ${s.area.d} m</dd>
      <dt>計画地盤高</dt><dd>T.P. ${fmt1(s.FH)} m${s.pit ? `／${esc(s.kind.pitLabel)} ${fmt1(s.pit)} m` : ''}</dd>
      <dt>現況地盤</dt><dd>T.P. ${fmt1(s.g.minH + s.yRef)}〜${fmt1(emax + s.yRef)} m（${esc(s.terrainSrc)}）</dd>
      <dt>表示高さ</dt><dd>${height}</dd>
      <dt>地域条件</dt><dd>雨天 ${s.region.rain}%／冬期 ${esc(winterTxt(s.region))}</dd></dl>
    <div class="kpis"><div class="kpi"><div class="v">${a}%</div><div class="l">実績（基準日）</div><div class="s">計画 ${pl}%（${a - pl >= 0 ? '+' : ''}${a - pl}pt）</div></div>
      <div class="kpi"><div class="v ${d > 14 ? 'late' : d > 0 ? 'warnc' : 'ahead'}">${d > 0 ? `+${d}` : d}<small style="font-size:11px;color:var(--muted)"> 日</small></div><div class="l">竣工 遅延予測</div><div class="s">予測 ${forecastDay(v.cur.end)}</div></div></div>
    <div class="btns"><button class="btn" id="goSite" data-view="site">現場を正面に</button><button class="btn ghost" id="goTop" data-view="goTop">真上から</button><button class="btn ghost" id="goNation" data-view="nation">◀ 全国へ</button></div>`;
}

export interface PortalHandle {
  /** たまっている描き直しを、いますぐ行う（テスト用）。 */
  flush(): void;
  dispose(): void;
}

export interface SceneElements {
  cz: HTMLElement;
  labels: HTMLElement;
  scalebar: HTMLElement;
  needle: SVGElement;
}
export type NetKind = 'init' | 'loading' | 'google' | 'simple' | 'error' | 'nolib';
export interface PortalSceneHooks {
  setStatus(kind: NetKind, msg?: string): void;
  toast(html: string, ms?: number): void;
  onConnectFailed(diag: { title: string; steps: string[]; raw: string }, key: string): void;
  pickPref(name: string): void;
  onGoogleChange(): void;
}
/** 画面の外から渡す部品。住所照合と 3D は省略できる。 */
export interface PortalOptions {
  address?: AddressIndex;
  /**
   * ビルドに含める Google のキー（既定は空。ADR-0004 決定 1/6）。空文字は「無い」として扱い、
   * 設定されていれば利用者が画面で入れたキーより優先する（サンプル `EMBED_GOOGLE_KEY || lsGet(KEY_STORE)` と同じ）。
   */
  builtInKey?: string;
  /** 3D ビューを作る。作れなければ null を返すか例外を投げる（そのときは 3D なしで続ける）。 */
  createScene?: (els: SceneElements, hooks: PortalSceneHooks) => Scene3D | null;
}

const NET_TEXT: Readonly<Record<NetKind, string>> = {
  init: '3D 準備中',
  loading: 'Google 3D 接続中…',
  google: 'Google 実写3D（オンライン）',
  simple: '簡易地形モード',
  error: 'Google 3D 未接続',
  nolib: '3D 表示不可',
};
const NET_TITLE: Partial<Record<NetKind, string>> = {
  google:
    '周辺の街並み・地形は Google Map Tiles API（Photorealistic 3D Tiles）。工事・計画はすべて架空です。',
  simple: 'Google 3D を使わず、国土地理院の標高データによる周辺1kmの地形で表示しています。',
};

const lsGet = (k: string): string | null => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const lsSet = (k: string, v: string | null): void => {
  try {
    if (v == null) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  } catch {
    // 保存できない環境（プライベートブラウズなど）では、保存せずに続ける
  }
};

/** ポータルの画面を root に組み立てる。 */
export function mountPortal(
  root: HTMLElement,
  store: Store,
  edition: Edition,
  options: PortalOptions = {},
): PortalHandle {
  root.innerHTML = shell(edition, store);
  const $ = <T extends Element = HTMLElement>(sel: string): T => {
    const el = root.querySelector<T>(sel);
    if (!el) throw new Error(`画面の部品がない: ${sel}`);
    return el;
  };
  const list = $('#list');
  const q = $<HTMLInputElement>('#q');
  const prefSel = $<HTMLSelectElement>('#prefSel');
  const range = $<HTMLInputElement>('#tlRange');
  const dockEl = $('#dock');
  const dockBody = $('#dockBody');
  const keyDlg = $('#keyDlg');

  type Part = 'ledger' | 'summary' | 'timeline' | 'dock' | 'dockTime' | 'chrome';
  const dirty = new Set<Part>(['ledger', 'summary', 'timeline', 'dock', 'chrome']);
  let frame: number | null = null;
  let last = 0;
  let tab: DockTab = 'earth';
  let dockOpen = true;
  let hits: PlaceHit[] = [];
  let toastTimer = 0;
  /** ドックの中身を組み立てた現場とタブ（同じあいだは入力中の欄を作り直さない）。 */
  let dockFor = '';

  const schedule = () => {
    if (frame === null) frame = requestAnimationFrame(loop);
  };
  const toast = (html: string, ms = 7000) => {
    const t = $('#toast');
    t.innerHTML = html;
    t.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => (t.hidden = true), ms);
  };
  const setStatus = (kind: NetKind, msg?: string) => {
    const el = $('#netStatus');
    el.className = `net ${kind}`;
    el.textContent = NET_TEXT[kind];
    el.title = msg ?? NET_TITLE[kind] ?? '';
    dirty.add('ledger').add('summary');
    schedule();
  };

  // ---- API キーの画面（FR-G3D-01・09） ----
  const openKeyDialog = (
    diag?: { title: string; steps: string[]; raw: string },
    tried?: string,
  ) => {
    keyDlg.hidden = false;
    $('#keyErr').hidden = true;
    const k = lsGet(KEY_STORE);
    $<HTMLInputElement>('#keyIn').value = tried ?? k ?? '';
    $<HTMLInputElement>('#keySave').checked = true;
    $('#keyForget').hidden = !k;
    const dg = $('#keyDiag');
    dg.hidden = !diag?.title;
    if (diag?.title) dg.innerHTML = keyDiagHtml(diag, location.href.split('#')[0]!.split('?')[0]!);
    window.setTimeout(() => $<HTMLInputElement>('#keyIn').focus(), 30);
  };
  const closeKeyDialog = () => (keyDlg.hidden = true);

  // ---- 3D ----
  let scene: Scene3D | null = null;
  const hooks: PortalSceneHooks = {
    setStatus,
    toast,
    onConnectFailed: (d, key) => openKeyDialog(d, key),
    pickPref: (name) => {
      store.setPref(name);
      if (scene && store.state.sel === null) {
        if (name) scene.flyPref(name);
        else scene.flyHome(1.6);
      }
    },
    onGoogleChange: () => {
      dirty.add('ledger').add('summary').add('chrome');
      schedule();
    },
  };
  if (options.createScene) {
    try {
      scene = options.createScene(
        {
          cz: $('#cz'),
          labels: $('#labels'),
          scalebar: $('#scalebar'),
          needle: $<SVGElement>('#needle'),
        },
        hooks,
      );
    } catch (e) {
      console.error(e);
      scene = null;
    }
    if (!scene) {
      const ng = $('#nogl');
      ng.hidden = false;
      ng.innerHTML =
        'このブラウザでは 3D 表示（WebGL2）を開始できませんでした。<br>台帳・土量・工程などの管理機能は利用できます。';
      setStatus('nolib');
    }
  }

  const chromeState = (): ChromeState => ({
    mode: scene?.mode ?? (store.selected ? 'site' : 'nation'),
    google: scene?.google ?? false,
    useG: scene?.useG() ?? false,
    autoRotate: scene?.autoRotate ?? false,
    layers: scene?.layers ?? {},
  });

  /**
   * 入力欄にフォーカスがある間は、欄そのものは作り直さず、欄の外の表示（能力・ボトルネック・
   * 出来高曲線・What-if の札など）だけを新しい計画で描き直す（サンプルの bindFields と同じ動き）。
   * これで「すぐに計算し直して表示する」（FR-SIM-04）を満たしつつ、入力中の値を失わない。
   */
  const refreshDockDynamic = (v: SiteView | null | undefined) => {
    const set = (id: string, html: string) => {
      const el = dockBody.querySelector(`#${id}`);
      if (el) el.innerHTML = html;
    };
    if (!v) {
      set('pfBody', portfolioTableHtml(store));
      set('pfGantt', portfolioGantt(store.inPref(), store.state.T));
      return;
    }
    const T = store.state.T;
    const changed = store.isWhatIf(v);
    if (tab === 'earth') {
      set('earthKpi', earthKpiHtml(v));
      set('heat', cutFillMap(v));
      set('earthBal', earthBalHtml(v));
    } else if (tab === 'plan') {
      set('capBox', capHtml(v));
      set('massBox', `${massCurve(v)}<div class="note">${massNote(v)}</div>`);
    } else if (tab === 'sched') {
      set('ganttBox', siteGantt(v, T));
      set('scurveBox', sCurve(v, T, changed));
      set('schedNote', schedNoteHtml(v));
    } else if (tab === 'wm') {
      set('wmState', wmStateHtml(v, T));
      set('wmWhatIf', wmWhatIfHtml(v));
    } else if (tab === 'const') {
      // 施工計画のタブは入力欄がないので、まるごと作り直してよい
      dockBody.innerHTML = dockTabsHtml(v, tab, store);
      return;
    }
    const badge = dockBody.querySelector('h3 .badge');
    if (badge) badge.outerHTML = whatIfNote(changed);
  };

  const render = () => {
    const { T, playing, pref, chip } = store.state;
    const v = store.selected;
    if (dirty.has('ledger')) {
      // Google 3D 表示中は Google 以外のジオコーディング結果を出さない（元仕様 §8.7・§9）
      const useG = scene?.useG() ?? false;
      hits =
        !useG && store.state.q.trim().length >= 2
          ? (options.address?.lookup(store.state.q) ?? [])
          : [];
      list.innerHTML = ledgerHtml(store, hits);
      $('#count').textContent = `表示 ${store.filtered().length} / ${store.views.length} 件`;
      $('#ledgerTitle').textContent = `現場台帳（${ds(T)}${T > B ? '・予測' : ''}）`;
      root.querySelectorAll<HTMLElement>('.chip').forEach((c) => {
        const on = c.dataset.chip === chip;
        c.classList.toggle('on', on);
        c.setAttribute('aria-pressed', String(on));
      });
      if (prefSel.value !== pref) prefSel.value = pref;
    }
    if (dirty.has('summary')) $('#summary').innerHTML = summaryHtml(store, scene);
    if (dirty.has('timeline')) {
      const pred = T > B;
      if (Number(range.value) !== Math.floor(T)) range.value = String(Math.floor(T));
      $('#tlDate').textContent = ds(T);
      const m = $('#tlMode');
      m.className = `tlmode ${pred ? 'pred' : 'obs'}`;
      m.textContent = pred ? '予測（モデル）' : '実績（観測）';
      $('#tlPlay').textContent = playing ? '⏸ 停止' : '▶ 再生';
      const w = v && v.state.winter && v.site.region.ws ? '・冬期' : '';
      const f = $('#modeflag');
      f.className = `modeflag ${pred ? 'pred' : 'obs'}`;
      f.textContent =
        (pred
          ? `予測（モデル） ${ds(T)}／基準日+${Math.floor(T - B)}日`
          : Math.floor(T) === B
            ? `実績（観測） ${ds(T)}`
            : `実績（観測・補間） ${ds(T)}`) + w;
    }
    if (dirty.has('chrome')) {
      const c = chromeState();
      $('#tools').innerHTML = toolsHtml(c);
      $('#layers').innerHTML = layersHtml(c);
      $('#help').innerHTML = helpHtml(c);
      $('#legend').innerHTML = legendHtml(c);
      const fe = $('#fiction');
      const ft = fictionText(c);
      fe.hidden = ft === null;
      if (ft) fe.textContent = ft;
    }
    if (dirty.has('dock') || dirty.has('dockTime')) {
      $('#dtabs').innerHTML = dockHeaderHtml(v, tab, store, dockOpen);
      const key = v ? `${v.site.id}|${tab}` : `nation|${pref}`;
      const focused = document.activeElement;
      // 入力中の欄があるあいだは、ドックを作り直さない（フォーカスと入力途中の値を保つ）
      const typing =
        !dirty.has('dock') &&
        key === dockFor &&
        focused instanceof HTMLElement &&
        dockBody.contains(focused) &&
        focused.matches('input,select');
      if (typing) {
        // 入力中の欄はそのままに、その外側の表示だけを新しい計画で描き直す
        refreshDockDynamic(v);
      } else {
        const ganttFocused = focused instanceof Element && focused.matches('svg.gantt');
        dockBody.innerHTML = dockTabsHtml(v, tab, store);
        dockFor = key;
        if (ganttFocused) root.querySelector<SVGSVGElement>('svg.gantt')?.focus();
      }
    }
    dirty.clear();
  };

  function loop(now: number) {
    frame = null;
    if (store.state.playing) {
      store.tick(last ? Math.min(0.25, (now - last) / 1000) : 0);
      last = now;
    } else last = 0;
    render();
    if (store.state.playing) schedule();
  }

  const WHAT: Record<Change, Part[]> = {
    time: ['ledger', 'timeline', 'dockTime'],
    filter: ['ledger', 'summary', 'dock'],
    selection: ['ledger', 'summary', 'timeline', 'dock', 'chrome'],
    plan: ['ledger', 'summary', 'dockTime'],
    planReset: ['ledger', 'summary', 'dock'],
    play: ['timeline'],
    mark: ['summary'],
  };
  let prevSel: string | null = store.state.sel;
  const unsubscribe = store.subscribe((changes) => {
    for (const c of changes) for (const part of WHAT[c]) dirty.add(part);
    if (scene) {
      if (changes.has('time') || changes.has('plan') || changes.has('planReset'))
        scene.sceneDirty = scene.terrainDirty = true;
      if (changes.has('plan') || changes.has('planReset')) scene.designChanged();
      if (changes.has('filter')) scene.mapDirty = true;
      if (changes.has('selection')) {
        const v = store.selected;
        if (v) scene.enterSite(v, prevSel === null);
        else if (scene.mode === 'site')
          scene.enterNation(prevSel ? store.view(prevSel) : undefined);
      }
      if (changes.has('mark') && store.state.mark)
        scene.flyPoint(store.state.mark.lon, store.state.mark.lat);
    }
    if (changes.has('selection')) {
      prevSel = store.state.sel;
      if (store.state.sel)
        list
          .querySelector<HTMLElement>(`.row[data-id="${CSS.escape(store.state.sel)}"]`)
          ?.scrollIntoView?.({ block: 'nearest' });
    }
    schedule();
  });

  // ---- 操作 ----
  const ganttClick = (svg: SVGSVGElement, e: MouseEvent) => {
    const v = store.selected;
    if (!v) return;
    const g = ganttGeom(v);
    const box = svg.getBoundingClientRect();
    if (!box.width) return;
    const day = dayAtGanttX(g, ((e.clientX - box.left) / box.width) * g.W);
    if (day !== null) store.setT(day);
  };
  const setLayer = (k: string, on: boolean) => {
    if (!scene) return;
    scene.setLayer(k, on);
    dirty.add('chrome');
    schedule();
  };
  const act = (a: string) => {
    if (a.startsWith('tab:')) {
      tab = a.slice(4) as DockTab;
      if (!dockOpen) {
        dockOpen = true;
        dockEl.classList.remove('closed');
      }
      dirty.add('dock');
    } else if (a === 'dock') {
      dockOpen = !dockOpen;
      dockEl.classList.toggle('closed', !dockOpen);
      dirty.add('dock');
    } else if (a === 'reset' && store.state.sel) store.resetPlan(store.state.sel);
    else if (a === 'play') {
      store.setT(B);
      if (!store.state.playing) store.togglePlay();
    } else if (a.startsWith('layer:')) setLayer(a.slice(6), true);
    schedule();
  };
  const view = (name: string) => {
    if (name === 'nation') return store.select(null);
    if (name === 'all') {
      if (store.state.pref) store.setPref('');
      scene?.flyHome();
      return;
    }
    if (name === 'spin') {
      if (scene) scene.autoRotate = !scene.autoRotate;
      dirty.add('chrome');
      return schedule();
    }
    scene?.applyView(name);
  };
  const keyOk = async () => {
    const k = $<HTMLInputElement>('#keyIn').value.trim();
    if (!KEY_RE.test(k)) {
      const e = $('#keyErr');
      e.textContent = 'APIキーの形式が正しくないようです（例：AIza で始まる39文字）。';
      e.hidden = false;
      return;
    }
    const save = $<HTMLInputElement>('#keySave').checked;
    closeKeyDialog();
    if (!scene) return;
    const ok = await scene.connectGoogle(k);
    // 保存はつながったときだけ。「保存する」を外したときは、前に保存したキーも消す（FR-G3D-01）
    if (ok) lsSet(KEY_STORE, save ? k : null);
  };
  const keySkip = () => {
    closeKeyDialog();
    if (!scene?.google) setStatus(scene ? 'simple' : 'nolib');
  };
  const onClick = (e: Event) => {
    const target = e.target as HTMLElement;
    const actEl = target.closest<HTMLElement>('[data-act]');
    if (actEl?.dataset.act) {
      if (!(actEl as HTMLButtonElement).disabled) act(actEl.dataset.act);
      return;
    }
    const viewEl = target.closest<HTMLElement>('[data-view]');
    if (viewEl?.dataset.view) return view(viewEl.dataset.view);
    const layerEl = target.closest<HTMLElement>('[data-layer]');
    if (layerEl?.dataset.layer) {
      if (scene) setLayer(layerEl.dataset.layer, !scene.layers[layerEl.dataset.layer]);
      return;
    }
    if (target.closest('#compass')) return scene?.northUp();
    const row = target.closest<HTMLElement>('[data-id]');
    if (row?.dataset.id) return store.select(row.dataset.id);
    const addr = target.closest<HTMLElement>('[data-addr]');
    if (addr) {
      const hit = hits[Number(addr.dataset.addr)];
      if (hit) store.setMark({ label: hit.label, lon: hit.lon, lat: hit.lat });
      return;
    }
    if (target.closest('[data-mark-clear]')) return store.setMark(null);
    const gantt = target.closest<SVGSVGElement>('svg.gantt');
    if (gantt && e instanceof MouseEvent) return ganttClick(gantt, e);
    const chip = target.closest<HTMLElement>('[data-chip]');
    if (chip?.dataset.chip) return store.setChip(chip.dataset.chip as Chip);
    if (target.closest('#tlNow')) return store.toBase();
    if (target.closest('#tlPlay')) return store.togglePlay();
    if (target.closest('#keyBtn')) return openKeyDialog();
    if (target.closest('#keyOk')) return void keyOk();
    if (target.closest('#keySkip')) return keySkip();
    if (target.closest('#keyForget')) {
      lsSet(KEY_STORE, null);
      $<HTMLInputElement>('#keyIn').value = '';
      $('#keyForget').hidden = true;
      scene?.disconnectGoogle();
    }
  };
  const onInput = (e: Event) => {
    if (e.target === q) store.setQuery(q.value);
    if (e.target === range) store.setT(range.valueAsNumber);
    // 施工計画は入力のたびに計算し直す。計画地盤高・根切り深さは確定（change）のときだけ
    const el = e.target as HTMLInputElement;
    const key = el.dataset?.plan;
    if (key && isPlanNumberKey(key) && store.state.sel)
      store.setPlanValue(store.state.sel, key, el.value);
  };
  /** 範囲に収めた値を入力欄へ書き戻す。数でない入力は、いまの値へ戻す。 */
  const syncField = (el: HTMLInputElement, value: number) => {
    const text = String(Math.round(value * 10) / 10);
    if (el.value !== text) el.value = text;
  };
  const onChange = (e: Event) => {
    const target = e.target as HTMLSelectElement;
    if (target === prefSel) {
      if (store.state.sel) store.select(null);
      store.setPref(prefSel.value);
      if (scene) {
        if (prefSel.value) scene.flyPref(prefSel.value);
        else scene.flyHome(1.6);
      }
    }
    if (target.id === 'tlSpeed') store.setSpeed(Number(target.value) as Speed);
    const v = store.selected;
    if (!v) return;
    const el = e.target as HTMLInputElement;
    const { plan, grade, soil } = el.dataset ?? {};
    if (soil) store.setSoil(v.site.id, el.value);
    if (plan && isPlanNumberKey(plan)) syncField(el, v.plan[plan]);
    if (grade && isGradeKey(grade)) {
      store.setGradeValue(v.site.id, grade, el.value);
      syncField(el, v.site[grade]);
    }
  };
  const onKey = (e: KeyboardEvent) => {
    if (!keyDlg.hidden) {
      if (e.target === root.querySelector('#keyIn')) {
        if (e.key === 'Enter') void keyOk();
        if (e.key === 'Escape') keySkip();
      }
      return;
    }
    const typing =
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLSelectElement ||
      e.target instanceof HTMLTextAreaElement;
    if (e.target === q) {
      if (e.key === 'Enter') {
        // 一致した現場があれば先頭の現場を開き、なければ住所照合の先頭へ印を置く（元仕様 §9）
        const first = store.filtered()[0];
        if (first && q.value.trim()) store.select(first.site.id);
        else if (!(scene?.useG() ?? false) && hits[0])
          store.setMark({ label: hits[0].label, lon: hits[0].lon, lat: hits[0].lat });
      }
      if (e.key === 'Escape') {
        q.value = '';
        store.setQuery('');
        q.blur();
      }
      return;
    }
    // ガントにフォーカスがあるとき、左右キーで表示日を動かす（FR-SCHED-04 をキーボードでも。NFR-A11Y-01）
    if (
      (e.key === 'ArrowLeft' || e.key === 'ArrowRight') &&
      e.target instanceof Element &&
      e.target.matches('svg.gantt')
    ) {
      e.preventDefault();
      store.setT(
        Math.floor(store.state.T) + (e.shiftKey ? 30 : 7) * (e.key === 'ArrowLeft' ? -1 : 1),
      );
      return;
    }
    // 条件の欄で Esc を押したら、入力を確定するだけにする（計画地盤高の入力を失わないため）
    if (typing && e.key === 'Escape' && (e.target as HTMLElement).closest('#dockBody')) {
      (e.target as HTMLElement).blur();
      return;
    }
    if (typing) return;
    if (e.key === '/') {
      e.preventDefault();
      q.focus();
    } else if (e.key === ' ' && !(e.target instanceof HTMLButtonElement)) {
      e.preventDefault();
      store.togglePlay();
    } else if (e.key === 'Home') view('all');
    else if (e.key === 'Escape' && store.state.sel) store.select(null);
  };
  // 欄から離れたら、収めた値と札をそろえるために描き直す
  const onFocusOut = (e: Event) => {
    if (e.target instanceof HTMLElement && e.target.closest('#dockBody')) schedule();
  };
  root.addEventListener('focusout', onFocusOut);
  root.addEventListener('click', onClick);
  root.addEventListener('input', onInput);
  root.addEventListener('change', onChange);
  document.addEventListener('keydown', onKey);

  render();
  if (scene) {
    // ビルドに含めたキーか、保存したキーがあれば Google 3D へ。
    // 順はサンプルと同じ「埋め込みが優先、無ければ保存」（`EMBED_GOOGLE_KEY || lsGet(KEY_STORE)`）。
    // 空文字は「無い」として扱うので、`??` ではなく真偽で選ぶ。ADR-0004 決定 1/6 は「既定を空にする」
    // であり、埋め込みを設定したときの優先順はこの決定に反しない（設定して本番に出すのは Approval PR）。
    // どちらも無ければ簡易地形で表示し、キーの画面を開く（サンプルと同じ）
    const key = edition.key === 'online' ? options.builtInKey || lsGet(KEY_STORE) || '' : '';
    if (key) void scene.connectGoogle(key);
    else {
      setStatus('simple');
      if (edition.key === 'online') openKeyDialog();
    }
  }
  return {
    flush: render,
    dispose() {
      unsubscribe();
      if (frame !== null) cancelAnimationFrame(frame);
      window.clearTimeout(toastTimer);
      root.removeEventListener('focusout', onFocusOut);
      root.removeEventListener('click', onClick);
      root.removeEventListener('input', onInput);
      root.removeEventListener('change', onChange);
      document.removeEventListener('keydown', onKey);
      root.innerHTML = '';
    },
  };
}
